import { ErrorBody } from '@cockpit/shared';
import type { z } from 'zod';

/** Erreur renvoyée par l'API (corps du CDC §3.1). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions<S extends z.ZodType | undefined> {
  body?: unknown;
  /** Schéma Zod partagé qui valide la réponse ; sans schéma, la réponse est ignorée. */
  schema?: S;
  fetch?: typeof fetch;
  /** En-têtes en plus (lot AW : `Idempotency-Key`, `X-Base-Updated-At` des changements rejoués). */
  headers?: Record<string, string>;
}

type Result<S> = S extends z.ZodType ? z.output<S> : void;

/**
 * Lot AW « Hors connexion » : couche branchée au démarrage (`stores/offline`). Les lectures de la liste blanche
 * sont gardées sur le téléphone et servies sans réseau ; sans réseau, les autres requêtes échouent tout de suite
 * (« Pas disponible hors connexion », « Disponible avec le réseau ») au lieu d'attendre.
 */
export interface OfflineLayer {
  /** Vrai quand on sait qu'il n'y a pas de réseau. */
  isOffline(): boolean;
  isCacheable(path: string): boolean;
  read(path: string): Promise<{ data: unknown; savedAt: string } | null>;
  write(path: string, data: unknown): void;
  /** Une réponse gardée a été affichée (bandeau « données de 14 h 32 »). */
  served(path: string, savedAt: string): void;
  /** Le réseau a manqué (`fetch` en échec) ou répond. */
  networkDown(): void;
  networkUp(): void;
  /** Transforme une réponse avant validation (changements pas encore envoyés). */
  transform(path: string, data: unknown): unknown;
}

let offlineLayer: OfflineLayer | null = null;

export function setOfflineLayer(layer: OfflineLayer | null): void {
  offlineLayer = layer;
}

export const OFFLINE_READ_MESSAGE = 'Pas disponible hors connexion.';
export const OFFLINE_WRITE_MESSAGE = 'Disponible avec le réseau.';

function networkError(): ApiError {
  return new ApiError(0, 'NETWORK_ERROR', 'Le serveur ne répond pas. Vérifiez votre connexion.');
}

function validate<S extends z.ZodType | undefined>(schema: S | undefined, data: unknown, status: number) {
  if (!schema) return undefined as Result<S>;
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new ApiError(
      status,
      'INVALID_RESPONSE',
      'Réponse du serveur non conforme au contrat.',
      result.error.issues,
    );
  }
  return result.data as Result<S>;
}

/** Réponse gardée sur le téléphone, ou erreur « pas disponible hors connexion ». */
async function fromCache<S extends z.ZodType | undefined>(
  layer: OfflineLayer,
  path: string,
  schema: S | undefined,
): Promise<Result<S>> {
  const hit = await layer.read(path);
  if (!hit) {
    throw new ApiError(
      0,
      'OFFLINE_NO_DATA',
      `${OFFLINE_READ_MESSAGE} Ces données n'ont pas encore été ouvertes sur cet appareil.`,
    );
  }
  const value = validate(schema, layer.transform(path, hit.data), 200);
  layer.served(path, hit.savedAt);
  return value;
}

export async function request<S extends z.ZodType | undefined = undefined>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions<S> = {},
): Promise<Result<S>> {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const layer = options.fetch ? null : offlineLayer;
  const cacheable = layer !== null && method === 'GET' && layer.isCacheable(path);
  if (layer?.isOffline()) {
    if (cacheable) return fromCache(layer, path, options.schema);
    throw new ApiError(0, 'OFFLINE', method === 'GET' ? OFFLINE_READ_MESSAGE : OFFLINE_WRITE_MESSAGE);
  }
  let response: Response;
  try {
    response = await doFetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...options.headers,
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    layer?.networkDown();
    if (layer && cacheable) return fromCache(layer, path, options.schema);
    throw networkError();
  }
  layer?.networkUp();

  const text = await response.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }

  if (!response.ok) {
    const parsed = ErrorBody.safeParse(data);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      throw new ApiError(response.status, code, message, details);
    }
    throw new ApiError(
      response.status,
      'HTTP_ERROR',
      `Erreur inattendue du serveur (HTTP ${response.status}).`,
    );
  }

  if (cacheable && layer) {
    // Gardée telle que le serveur l'a rendue (après validation), puis montrée avec les changements en attente.
    const checked = validate(options.schema, data, response.status);
    layer.write(path, data);
    return options.schema ? validate(options.schema, layer.transform(path, data), response.status) : checked;
  }
  return validate(options.schema, data, response.status);
}

/** Erreur d'une réponse non JSON-attendue : lit le corps d'erreur du §3.1 s'il y en a un. */
export async function errorFrom(response: Response): Promise<ApiError> {
  let data: unknown = undefined;
  try {
    data = JSON.parse(await response.text());
  } catch {
    data = undefined;
  }
  const parsed = ErrorBody.safeParse(data);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiError(response.status, code, message, details);
  }
  return new ApiError(
    response.status,
    'HTTP_ERROR',
    `Erreur inattendue du serveur (HTTP ${response.status}).`,
  );
}

/** GET d'une réponse texte (ex. l'export des retours). */
export async function requestText(
  path: string,
  doFetch: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<string> {
  let response: Response;
  try {
    response = await doFetch(path, { credentials: 'same-origin', headers: { accept: 'text/plain' } });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Le serveur ne répond pas. Vérifiez votre connexion.');
  }
  if (!response.ok) throw await errorFrom(response);
  return response.text();
}

/** Envoi d'un fichier brut (corps binaire, type du fichier en en-tête), réponse validée par `schema`. */
export async function uploadFile<S extends z.ZodType>(
  path: string,
  file: Blob,
  schema: S,
  doFetch: typeof fetch = globalThis.fetch.bind(globalThis),
  headers: Record<string, string> = {},
): Promise<z.output<S>> {
  if (offlineLayer?.isOffline()) throw new ApiError(0, 'OFFLINE', OFFLINE_WRITE_MESSAGE);
  let response: Response;
  try {
    response = await doFetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        'content-type': file.type || 'application/octet-stream',
        ...headers,
      },
      body: file,
    });
  } catch {
    offlineLayer?.networkDown();
    throw networkError();
  }
  if (!response.ok) throw await errorFrom(response);
  const result = schema.safeParse(await response.json());
  if (!result.success) {
    throw new ApiError(response.status, 'INVALID_RESPONSE', 'Réponse du serveur non conforme au contrat.');
  }
  return result.data;
}

/** Envoi d'un formulaire multipart (ex. fichier .xlsx d'un import), réponse validée par `schema`. */
export async function uploadForm<S extends z.ZodType>(
  path: string,
  form: FormData,
  schema: S,
  doFetch: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<z.output<S>> {
  let response: Response;
  try {
    // Pas d'en-tête content-type : le navigateur pose le multipart avec sa frontière.
    response = await doFetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
      body: form,
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Le serveur ne répond pas. Vérifiez votre connexion.');
  }
  if (!response.ok) throw await errorFrom(response);
  const result = schema.safeParse(await response.json());
  if (!result.success) {
    throw new ApiError(response.status, 'INVALID_RESPONSE', 'Réponse du serveur non conforme au contrat.');
  }
  return result.data;
}
